import React from 'react';
import { Inbox, LucideIcon, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from './shadcn/button';
import { colors, spacing, borderRadius } from '../../styles/design-tokens';

export interface EmptyStateProps {
  /** 아이콘 (lucide-react LucideIcon 컴포넌트 또는 ReactNode). 미지정 시 기본 Inbox 아이콘. */
  icon?: LucideIcon | React.ReactNode;
  /** 메인 안내 문구 */
  title: string;
  /** 보조 설명 (선택) */
  description?: string;
  /** 액션 영역 (보통 신규 생성 버튼 등) */
  action?: React.ReactNode;
  /** 변형: 'empty'(기본·데이터 없음) | 'error'(에러 상태) */
  variant?: 'empty' | 'error';
  /** 에러 variant 에서 재시도 콜백. 지정 시 기본 재시도 버튼 표시 */
  onRetry?: () => void;
  /** 세로 여백 커스텀 (기본 spacing.xl) */
  padding?: string;
}

/**
 * 리스트/테이블/컬렉션에서 데이터가 없거나 에러일 때 공용 안내 화면
 *
 * 사용 예:
 *   <EmptyState title="등록된 BOM이 없습니다" icon={GitBranch} />
 *   <EmptyState variant="error" title="데이터를 불러오지 못했습니다" description="잠시 후 다시 시도해주세요" onRetry={load} />
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  variant = 'empty',
  onRetry,
  padding,
}) => {
  const isError = variant === 'error';
  const DefaultIcon = isError ? AlertCircle : Inbox;

  // icon prop 이 LucideIcon(컴포넌트) 인지 ReactNode 인지 판별
  const iconNode = React.useMemo(() => {
    if (!icon) {
      return <DefaultIcon style={{ height: 22, width: 22, color: isError ? 'var(--ws-red)' : 'var(--ws-ink-3)' }} strokeWidth={1.7} />;
    }
    if (React.isValidElement(icon)) return icon;
    // LucideIcon (함수 컴포넌트) 가정
    const IconComp = icon as LucideIcon;
    return <IconComp style={{ height: 22, width: 22, color: isError ? 'var(--ws-red)' : 'var(--ws-ink-3)' }} strokeWidth={1.7} />;
  }, [icon, DefaultIcon, isError]);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: padding ?? spacing.xl,
    paddingBottom: padding ?? spacing.xl,
    color: 'var(--ws-ink-3)',
    textAlign: 'center',
  };

  // 2026-10-14 워크스페이스 디자인 — 옅은 네모 칸 안의 가는 아이콘(레퍼런스의 점수 칸 모양)
  const iconWrapperStyle: React.CSSProperties = {
    width: 46,
    height: 46,
    display: 'grid',
    placeItems: 'center',
    backgroundColor: isError ? 'var(--ws-red-bg)' : 'var(--ws-subtle)',
    border: `1px solid ${isError ? 'var(--ws-red-line)' : 'var(--ws-line)'}`,
    borderRadius: 12,
    marginBottom: 14,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '15.5px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: isError ? 'var(--ws-red-ink)' : 'var(--ws-ink)',
    marginBottom: description ? spacing.xs : 0,
  };

  const descStyle: React.CSSProperties = {
    fontSize: '13.5px',
    lineHeight: 1.55,
    color: 'var(--ws-ink-3)',
    marginBottom: action || onRetry ? spacing.base : 0,
    maxWidth: 420,
  };

  return (
    <div role="status" style={containerStyle}>
      <div style={iconWrapperStyle}>{iconNode}</div>
      <p style={titleStyle}>{title}</p>
      {description && <p style={descStyle}>{description}</p>}
      {action && <div style={{ marginTop: spacing.sm }}>{action}</div>}
      {!action && isError && onRetry && (
        <div style={{ marginTop: spacing.sm }}>
          <Button variant="ghost" className="ws-btn ws-btn--sm" onClick={onRetry}>
            <RotateCcw size={14} aria-hidden />
            다시 시도
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmptyState;
